import { useQuery } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api, formatPrice } from "@/lib/api";

/** Live profit card. `kind` = "class" | "booking". */
export default function ProfitCard({ kind, id }) {
  const { data, isLoading } = useQuery({
    queryKey: ["profit", kind, id],
    queryFn: async () => (await api.get(`/admin/${kind === "class" ? "classes" : "bookings"}/${id}/profit`)).data,
    enabled: !!id,
    refetchInterval: 30_000,
  });
  if (!id) return null;
  if (isLoading || !data) {
    return <div className="rounded-xl border border-gg-border bg-gg-bg p-3 text-xs text-gg-muted">Loading profit…</div>;
  }
  const revenue = kind === "class" ? data.revenue?.total_revenue || 0 : data.revenue || 0;
  const expenses = data.expenses_total || 0;
  const profit = data.profit || 0;
  const pps = data.profit_per_student;
  const Icon = profit > 0 ? TrendingUp : profit < 0 ? TrendingDown : Minus;
  const tone = profit > 0 ? "text-gg-green" : profit < 0 ? "text-[#B24C4C]" : "text-gg-brown";

  return (
    <div className="rounded-xl border border-gg-border bg-gradient-to-br from-gg-surface to-gg-bg p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
          📈 Profit ({kind})
        </p>
        {data.revenue?.projected && (
          <span className="text-[10px] uppercase tracking-[0.12em] bg-[#D9933B]/15 text-[#9b6a26] px-2 py-0.5 rounded">Projected</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Revenue" value={formatPrice(revenue)} />
        <Stat label="Expenses" value={formatPrice(expenses)} tone="text-[#9b6a26]" />
        <div className="rounded-lg bg-gg-surface border border-gg-border p-2">
          <div className="text-[10px] uppercase tracking-[0.14em] text-gg-brown">Profit</div>
          <div className={`font-heading text-xl ${tone} flex items-center justify-center gap-1 mt-0.5`}>
            <Icon size={14} /> {formatPrice(profit)}
          </div>
        </div>
      </div>
      {kind === "class" && data.revenue?.student_count > 0 && (
        <div className="mt-3 text-center text-xs text-gg-muted">
          <span className="font-semibold text-[var(--gg-text)]">{data.revenue.student_count}</span> student{data.revenue.student_count === 1 ? "" : "s"} ·
          <span className={`ml-2 font-semibold ${tone}`}>{formatPrice(pps)}/student profit</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "text-[var(--gg-text)]" }) {
  return (
    <div className="rounded-lg bg-gg-surface border border-gg-border p-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-gg-brown">{label}</div>
      <div className={`font-heading text-base ${tone} mt-0.5`}>{value}</div>
    </div>
  );
}
